import { TFunction } from 'i18next';
import { orderBy, sum } from 'lodash';
import sortBy from 'lodash.sortby';
import { parse, toSql } from 'pgsql-ast-parser/lib';
import * as ast from 'pgsql-ast-parser/lib/syntax/ast';
import { match, P } from 'ts-pattern';
import { HierarchyItem } from '../../common/components/filter/types';
import {
  Granularity,
  Months,
  TimeSliderState,
} from '../../common/components/timeslider/types';
import {
  CONFIDENTIAL_VALUE,
  Domains,
  employmentTemporalityField,
  managerField,
  startCalendarYearField,
  startDateField,
  startFinancialYearField,
  UNDEFINED_DISPLAY_KEY,
} from '../../constants';
import { getMomentDate } from '../../date/date-utils';
import {
  DataFields,
  DataFieldWithDataType,
  DataTypes,
  EvaluationDataFields,
  Operations,
} from '../../types';
import {
  fieldSorter,
  getKeyFromDataFieldWithDataType,
  hashCode,
  hierarchicalFieldFullLabel,
  hierarchicalFieldLabel,
  isHierarchical,
  nonHierarchicalFieldLabel,
} from '../../utils';
import { EmployeeService } from '../employee/service';
import { Employee } from '../employee/types';
import { Hasher } from '../hasher';
import { DomainPreferences, hashString, PerformanceCycle } from '../types';
import { getFilterKey, parseCustomSqlValue } from '../utils';
import { DataService } from './service';
import {
  CustomSqlQueryValueBackendType,
  EMPLOYMENT_TEMPORALITY,
  FilterDataManager,
  Ord,
  Ordering,
} from './types';

export const getDataHierarchy = (
  levelsOfEntities: (string | null)[][],
  dimension: DataFieldWithDataType
): HierarchyItem[] => {
  let tree: HierarchyItem[] = buildHierarchyItems(
    removeConfidentialBranches(levelsOfEntities),
    dimension
  );
  tree = removeUnnecessaryNulls(tree);
  tree = unsetLeafSubitems(tree);
  return tree;
};

/**
 * After running this, the hierarchy looks something like
 * [
 * [Design, TechDesign, Confidential]
 * [Design, FinDesign, Confidential]
 * [Marketing, DigitalMarketing, Confidential]
 * ]
 */
const removeConfidentialBranches = (
  hierarchy: (string | null)[][]
): (string | null)[][] =>
  hierarchy.map((row) => {
    const confidentialIndex = row.indexOf(CONFIDENTIAL_VALUE);
    const nonConfidentalRow =
      confidentialIndex === -1 ? row : row.slice(0, confidentialIndex);
    // i.e. we remove everything from the hierarchy after the first confidential value
    return nonConfidentalRow;
  });

const buildHierarchyItems = (
  levelsOfEntities: (string | null)[][],
  dimension: DataFieldWithDataType
) => {
  const tree: HierarchyItem[] = [];
  levelsOfEntities.forEach((entity) => {
    addEntityToTree(tree, entity, 0, dimension);
  });
  return tree;
};

const findItemInTreeByName = (
  tree: HierarchyItem[],
  name: string | null
): HierarchyItem | null => {
  return tree.find((item) => item.name === name) ?? null;
};

// WARNING: self-recursive method!
const addEntityToTree = (
  tree: HierarchyItem[],
  levels: (string | null)[],
  level: number,
  dimension: DataFieldWithDataType
) => {
  // we have reached the max level
  if (levels.length <= level) {
    return;
  }
  const levelValue: string | null = levels[level];

  let next: HierarchyItem;

  const maybeItem = findItemInTreeByName(tree, levelValue);
  if (maybeItem) {
    next = maybeItem;
  } else {
    const values = levels.slice(0, level + 1);
    const newItem: HierarchyItem = {
      key: getFilterKey(dimension, values),
      values,
      name: levelValue?.toString() ?? null,
      dimension,
      labelFn: (t, defaultValue, full) => {
        return full
          ? hierarchicalFieldFullLabel(t, values, dimension, defaultValue)
          : hierarchicalFieldLabel(t, levelValue, dimension, defaultValue);
      },
      isSelectable: true,
      subItems: [],
    };
    tree.push(newItem);
    next = newItem;
  }

  addEntityToTree(next.subItems ?? [], levels, level + 1, dimension);
};

const unsetLeafSubitems = (tree: HierarchyItem[]) => {
  tree.forEach((elem) => {
    if (elem.subItems && elem.subItems.length === 0) {
      delete elem.subItems;
    } else {
      if (elem.subItems) {
        elem.subItems = unsetLeafSubitems(elem.subItems);
      }
    }
  });
  return tree;
};

const removeUnnecessaryNulls = (tree: HierarchyItem[]): HierarchyItem[] => {
  return tree.filter((t) => {
    if (t.subItems) {
      t.subItems = removeUnnecessaryNulls(t.subItems);
    }
    if (t.name) {
      return true;
    } else {
      return tree.length > 1 || (t.subItems && t.subItems.length > 0);
    }
  });
};

const sortHierarchy = (
  hierarchyItem: HierarchyItem[],
  preferences: DomainPreferences,
  level: number
): HierarchyItem[] => {
  if (hierarchyItem.length === 0) {
    return [];
  }
  const sortedSubHierarchy: HierarchyItem[] = hierarchyItem.map((item) => {
    if (item.subItems && item.subItems.length > 0) {
      return {
        ...item,
        subItems: sortHierarchy(item.subItems, preferences, level + 1),
      };
    } else {
      return item;
    }
  });
  const dimension = hierarchyItem[0].dimension;
  const field = isHierarchical(dimension)
    ? {
        ...dimension,
        dataField: `${dimension.dataField}_LEVEL_${level}` as DataFields,
      }
    : dimension;
  const sortOrder = fieldSorter(field, preferences);
  if (sortOrder) {
    return sortBy(sortedSubHierarchy, (i) => {
      const index = sortOrder.indexOf(i.name);
      return index === -1 ? null : index;
    });
  } else {
    return sortedSubHierarchy.sort((a, b) => hierarchyItemOrd.compare(a, b));
  }
};

export const fieldToSql = (dataField: DataFieldWithDataType): string =>
  isHierarchical(dataField)
    ? hierarchicalfieldToExprs(dataField)
        .map((r: ast.ExprRef) => ({ expr: r }))
        .map((r: ast.SelectedColumn) => toSql.selectionColumn(r))
        .join(',')
    : toSql.selectionColumn({ expr: nonHierarchicalfieldToExpr(dataField) });

// WARNING: this only handles 10 hierarhical levels
export const hierarchicalfieldToExprs = (
  datafield: DataFieldWithDataType
): ast.ExprRef[] => {
  return Array(10)
    .fill(0)
    .map((_, i) => ({
      type: 'ref',
      table: { name: datafield.dataType.toUpperCase() },
      name: `${datafield.dataField.toUpperCase()}_LEVEL_${i + 1}`,
    }));
};

export const hierarchicalfieldToExpr = (
  datafield: DataFieldWithDataType
): ast.ExprRef => {
  return {
    type: 'ref',
    table: { name: datafield.dataType.toUpperCase() },
    name: `${datafield.dataField.toUpperCase()}_LEVEL_1`,
  };
};

export const nonHierarchicalfieldToExpr = (
  datafield: DataFieldWithDataType
): ast.ExprRef => {
  return {
    type: 'ref',
    table: { name: datafield.dataType.toUpperCase() },
    name: datafield.dataField.toUpperCase(),
  };
};

export const dataTypeToExpr = (dataType: DataTypes): ast.Expr =>
  parse(`"${dataType.toUpperCase()}"`, 'expr');

export const getQuartersInRange = (
  start: { year: number; quarterOfYear: number },
  end: { year: number; quarterOfYear: number }
): { year: number; quarterOfYear: number }[] => {
  const quartersInRange: { year: number; quarterOfYear: number }[] = [];
  for (let year = start.year; year <= end.year; year++) {
    const startQuarter = year === start.year ? start.quarterOfYear : 1;
    const endQuarter = year === end.year ? end.quarterOfYear : 4;
    for (let quarter = startQuarter; quarter <= endQuarter; quarter++) {
      quartersInRange.push({ year, quarterOfYear: quarter });
    }
  }
  return quartersInRange;
};

export const timeSliderStateToExpr = (
  timeSliderState: TimeSliderState,
  range = false,
  dataType: DataTypes = DataTypes.EMPLOYEE
): ast.Expr => {
  return parse(
    match(timeSliderState)
      .with(
        {
          selectedGranularity: { value: Granularity.MONTH },
          start: P.instanceOf(Date),
          end: P.instanceOf(Date),
        },
        (ts) => {
          if (range) {
            return `"${dataType}"."VERSION_ID" BETWEEN '${getMomentDate(
              ts.start
            ).format(
              'YYYY-MM-DD'
            )}' AND '${getMomentDate(ts.end).format('YYYY-MM-DD')}'`;
          } else {
            return `"${dataType}"."VERSION_ID" = '${getMomentDate(ts.end).format('YYYY-MM-DD')}'`;
          }
        }
      )
      .with(
        {
          selectedGranularity: { value: Granularity.YEAR },
          start: P.number,
          end: P.number,
        },
        (ts) => {
          if (range) {
            return `"${dataType}"."CALENDAR_YEAR" BETWEEN ${ts.start} AND ${ts.end}`;
          } else {
            return `"${dataType}"."CALENDAR_YEAR" = ${ts.end}`;
          }
        }
      )
      .with(
        {
          selectedGranularity: { value: Granularity.FINYEAR },
          start: P.number,
          end: P.number,
        },
        (ts) => {
          if (range) {
            return `"${dataType}"."FINANCIAL_YEAR" BETWEEN ${ts.start} AND ${ts.end}`;
          } else {
            return `"${dataType}"."FINANCIAL_YEAR" = ${ts.end}`;
          }
        }
      )
      .with(
        {
          selectedGranularity: { value: Granularity.FINQUARTER },
          start: { year: P.number, quarterOfYear: P.number },
          end: { year: P.number, quarterOfYear: P.number },
        },
        (ts) => {
          if (range) {
            const quartersInRange = getQuartersInRange(ts.start, ts.end);
            // This is done purely for performance reason
            const yearConstraint = `"${dataType}"."FINANCIAL_YEAR" >= ${ts.start.year} AND "${dataType}"."FINANCIAL_YEAR" <= ${ts.end.year}`;
            return `(${quartersInRange
              .map((quarter) => {
                return `("${dataType}"."FINANCIAL_YEAR" = ${quarter.year} AND "${dataType}"."FINANCIAL_QUARTER" = ${quarter.quarterOfYear})`;
              })
              .join(' OR ')}) AND ${yearConstraint}`;
          } else {
            return `"${dataType}"."FINANCIAL_YEAR" = ${ts.end.year} AND "${dataType}"."FINANCIAL_QUARTER" = ${ts.end.quarterOfYear}`;
          }
        }
      )
      .otherwise(() => {
        throw new Error('Invalid timeSliderState');
      }),
    'expr'
  );
};

export const getHierarchyLength = (hierarchyItem: HierarchyItem[]): number => {
  return (
    hierarchyItem.filter((i) => i.isSelectable).length +
    sum(
      hierarchyItem.flatMap((h) =>
        h ? [getHierarchyLength(h.subItems ?? [])] : [0]
      )
    )
  );
};

export const showEmployee = (emp: Employee) =>
  `${emp.localFullName ?? emp.fullName ?? emp.id}${emp.jobTitle ? ` - ${emp.jobTitle}` : ''}`;

export class ManagerFilterDataManager implements FilterDataManager<'manager'> {
  private nonHierarchicalFilterDataManager: NonHierarchicalFilterDataManager;

  constructor(
    readonly employeeService: EmployeeService,
    readonly dataService: DataService,
    readonly timeSliderState: TimeSliderState,
    readonly preferences: DomainPreferences,
    readonly firstMonthOfYear: Months
  ) {
    this.nonHierarchicalFilterDataManager =
      new NonHierarchicalFilterDataManager(
        managerField,
        dataService,
        timeSliderState,
        preferences,
        firstMonthOfYear
      );
  }

  queryKey = [
    'get-datafield-values-manager',
    this.timeSliderState.start,
    this.timeSliderState.end,
    this.timeSliderState.selectedGranularity,
    managerField,
  ];

  fetch = async () => ({
    manager: (await this.nonHierarchicalFilterDataManager.fetch())[
      'nonHierarchical'
    ],
  });

  process = async (
    data: Record<'manager', CustomSqlQueryValueBackendType[][] | undefined>
  ) => {
    const result = await this.nonHierarchicalFilterDataManager.process({
      nonHierarchical: data['manager'],
    });
    const employeeIds = result.map((d) => d.name);
    const employees = await this.employeeService.list(
      employeeIds.flatMap((e) => (e ? [e] : [])),
      this.timeSliderState,
      this.firstMonthOfYear
    );
    const employeesById = Object.fromEntries(employees.map((e) => [e.id, e]));

    return result.map((r) => {
      const employeeId = r.name;
      const employee = employeeId ? employeesById[employeeId] : null;
      return {
        ...r,
        labelFn: employee ? () => showEmployee(employee) : r.labelFn,
      };
    });
  };

  count = (hierarchyItem: HierarchyItem[]) =>
    this.nonHierarchicalFilterDataManager.count(hierarchyItem);
}

export class NonHierarchicalFilterDataManager
  implements FilterDataManager<'nonHierarchical'>
{
  constructor(
    readonly dimension: DataFieldWithDataType,
    readonly dataService: DataService,
    readonly timeSliderState: TimeSliderState,
    readonly preferences: DomainPreferences,
    readonly firstMonthOfYear: Months
  ) {}

  queryKey = [
    'get-datafield-values',
    hashString([
      Hasher.hash(this.dimension),
      ...this.dataService.getDeps(),
      Hasher.hash(this.timeSliderState),
      Hasher.hash(this.preferences),
      this.firstMonthOfYear,
    ]),
  ];

  fetch = async () => {
    return {
      nonHierarchical: await this.dataService.queryDataFieldValues(
        [this.dimension],
        this.timeSliderState,
        this.firstMonthOfYear,
        true
      ),
    };
  };
  process = (
    data: Record<
      'nonHierarchical',
      CustomSqlQueryValueBackendType[][] | undefined
    >
  ): Promise<HierarchyItem[]> => {
    const result =
      data['nonHierarchical']?.flatMap((d) =>
        d.map((v) => {
          const value = parseCustomSqlValue(v)?.toString() ?? null;
          const item: HierarchyItem = {
            key: getFilterKey(this.dimension, [value]),
            name: value?.toString() ?? null,
            values: [value],
            dimension: this.dimension,
            labelFn: (t, defaultValue) => {
              return nonHierarchicalFieldLabel(
                t,
                value,
                this.dimension,
                defaultValue
              );
            },
            isSelectable: true,
          };
          return item;
        })
      ) ?? [];
    return Promise.resolve(sortHierarchy(result, this.preferences, 1));
  };

  count = (hierarchyItem: HierarchyItem[]) => getHierarchyLength(hierarchyItem);
}

export class EmployeeFilterDataManager
  implements FilterDataManager<'employee'>
{
  queryKey = [];
  fetch = () => Promise.resolve({ employee: undefined });
  process = () => Promise.resolve([]);
  count = () => 0;
}

export class HierarchicalFilterDataManager
  implements FilterDataManager<'hierarchical'>
{
  constructor(
    readonly dimension: DataFieldWithDataType,
    readonly dataService: DataService,
    readonly timeSliderState: TimeSliderState,
    readonly preferences: DomainPreferences,
    readonly firstMonthOfYear: Months
  ) {}

  queryKey = [
    'get-datafield-values',
    hashString([
      Hasher.hash(this.dimension),
      ...this.dataService.getDeps(),
      Hasher.hash(this.timeSliderState),
      Hasher.hash(this.preferences),
      this.firstMonthOfYear,
    ]),
  ];

  fetch = async () => {
    return {
      hierarchical: await this.dataService.queryDataFieldValues(
        [this.dimension],
        this.timeSliderState,
        this.firstMonthOfYear,
        true
      ),
    };
  };

  process = (
    data: Record<'hierarchical', CustomSqlQueryValueBackendType[][] | undefined>
  ): Promise<HierarchyItem[]> => {
    const entities: string[][] =
      data['hierarchical']?.map((d) =>
        d.map((v) => parseCustomSqlValue(v) as string)
      ) ?? [];
    const hierarchy = getDataHierarchy(entities, this.dimension).flatMap((i) =>
      i === null ? [] : [i]
    );
    return Promise.resolve(sortHierarchy(hierarchy, this.preferences, 1));
  };

  count = (hierarchyItem: HierarchyItem[]) => getHierarchyLength(hierarchyItem);
}

export class EvaluationFilterDataManager
  implements FilterDataManager<'evaluation'>
{
  constructor(
    readonly dataService: DataService,
    readonly performanceCycles: PerformanceCycle[],
    readonly domain: Domains,
    readonly useOldFilters?: boolean
  ) {}

  private evaluationCycleType = {
    dataType: DataTypes.EVALUATION,
    dataField: EvaluationDataFields.EVALUATION_CYCLE_TYPE,
  };
  private evaluationCycleDate = {
    dataType: DataTypes.EVALUATION,
    dataField: EvaluationDataFields.EVALUATION_AS_OF,
  };
  private evaluationCycleName = {
    dataType: DataTypes.EVALUATION,
    dataField: EvaluationDataFields.EVALUATION_CYCLE_NAME,
  };
  private evaluationScore = {
    dataType: DataTypes.EVALUATION,
    dataField: EvaluationDataFields.EVALUATION_SCORE,
  };

  // The order of the fields is important
  private fields = [
    this.evaluationCycleType,
    this.evaluationCycleDate,
    this.evaluationCycleName,
  ];

  queryKey = [
    'get-datafield-values',
    this.domain,
    hashString([
      ...this.fields.map((f) => Hasher.hash(f)),
      ...this.dataService.getDeps(),
      Hasher.hash(this.performanceCycles),
      this.useOldFilters?.toString(),
    ]),
  ];

  fetch = async () => {
    return {
      evaluation: await this.dataService.queryDataFieldValues(this.fields),
    };
  };

  process = (
    data: Record<'evaluation', CustomSqlQueryValueBackendType[][] | undefined>
  ) => {
    const dimension = {
      dataType: DataTypes.EVALUATION,
      dataField: EvaluationDataFields.EVALUATION_CYCLE_TYPE,
    };
    const evaluationData =
      data['evaluation']?.map((d) => {
        const [type, date, name] = d.map(parseCustomSqlValue) as [
          string,
          string,
          string,
        ];
        return { type, date, name };
      }) ?? [];
    const sortedEvaluationData = orderBy(
      evaluationData,
      ['date', 'type'],
      ['desc', 'asc']
    );
    const filters = sortedEvaluationData.reduce<
      Record<string, HierarchyItem[]>
    >((acc, { type, date, name }) => {
      const cycleInfo = this.performanceCycles.find(
        (cycle: PerformanceCycle) =>
          cycle.cycleType === type && cycle.cycleDate === date
      );
      if (!cycleInfo) {
        return acc;
      } else {
        const { evaluationValuesToScoreMap, scoreSortingOrder } = cycleInfo;
        const sortedEvaluationValues = sortBy(
          Object.keys(evaluationValuesToScoreMap),
          (item) => {
            return (scoreSortingOrder ?? []).indexOf(item);
          }
        );
        const scoreFilters: HierarchyItem[] = sortedEvaluationValues.map(
          (key) => {
            const score = evaluationValuesToScoreMap[key];
            return this.evaluationFilterItem(
              type,
              date,
              score,
              name,
              key,
              this.useOldFilters
            );
          }
        );

        return { ...acc, [name]: [...(acc[name] ?? []), ...scoreFilters] };
      }
    }, {});

    const hierarchyItem = Object.entries(filters).map(
      ([cycleName, cycleFilters]) => ({
        key: hashCode(`evaluation-cycle-${cycleName}`),
        labelFn: () => cycleName,
        values: [],
        dimension,
        name: cycleName,
        subItems: cycleFilters,
        isSelectable: false,
      })
    );
    return Promise.resolve(hierarchyItem);
  };

  private evaluationFilterItem = (
    type: string,
    date: string,
    score: number,
    name: string,
    key: string,
    useOldFilters?: boolean //TODO: this is temporary for movement db sql filters cannot be used
  ): HierarchyItem => {
    const dimension = {
      dataType: DataTypes.EVALUATION,
      dataField: EvaluationDataFields.EVALUATION_CYCLE_TYPE,
    };
    const base: HierarchyItem = {
      key: hashCode(
        `${getKeyFromDataFieldWithDataType(dimension)}-${type}-${date}-${name}-${score}`
      ),
      name: score.toString(),
      values: [`${type}-${date}-${name}-${score}`],
      dimension,
      labelFn: (_1, _2, full) => (full ? `${name}: ${key}` : key),
      isSelectable: true,
    };
    return useOldFilters
      ? {
          ...base,
          filterValues: [
            {
              dataType: DataTypes.EVALUATION,
              operation: Operations.EQUAL,
              property: EvaluationDataFields.EVALUATION_CYCLE_TYPE,
              values: [type],
            },
            {
              dataType: DataTypes.EVALUATION,
              operation: Operations.EQUAL,
              property: EvaluationDataFields.EVALUATION_AS_OF,
              values: [date],
            },
            {
              dataType: DataTypes.EVALUATION,
              operation: Operations.EQUAL,
              property: EvaluationDataFields.EVALUATION_SCORE,
              values: [score],
            },
          ],
        }
      : {
          ...base,
          sql: `(${fieldToSql(this.evaluationCycleType)} = '${type}' AND ${fieldToSql(
            this.evaluationCycleDate
          )} = '${date}' AND ${fieldToSql(this.evaluationScore)} = ${score})`,
          sqlOperator: 'OR',
        };
  };

  count = (hierarchyItem: HierarchyItem[]) =>
    sum(hierarchyItem.map((i) => i.subItems?.length ?? 0));
}

export class HireYearFilterDataManager
  implements FilterDataManager<'calendarYear' | 'financialYear'>
{
  private calendarYearManager: NonHierarchicalFilterDataManager;
  private financialYearManager: NonHierarchicalFilterDataManager | null;

  constructor(
    readonly dataService: DataService,
    readonly timeSliderState: TimeSliderState,
    readonly preferences: DomainPreferences,
    readonly firstMonthOfYear: Months
  ) {
    this.calendarYearManager = new NonHierarchicalFilterDataManager(
      startCalendarYearField,
      dataService,
      timeSliderState,
      preferences,
      firstMonthOfYear
    );

    // Only create financial year manager if fiscal year is configured
    this.financialYearManager =
      firstMonthOfYear === Months.January
        ? null
        : new NonHierarchicalFilterDataManager(
            startFinancialYearField,
            dataService,
            timeSliderState,
            preferences,
            firstMonthOfYear
          );
  }

  queryKey = [
    'get-datafield-values-hire-year',
    hashString([
      ...this.dataService.getDeps(),
      Hasher.hash(this.timeSliderState),
      Hasher.hash(this.preferences),
      this.firstMonthOfYear,
    ]),
  ];

  fetch = async () => {
    const result = await Promise.all([
      this.calendarYearManager.fetch(),
      this.financialYearManager
        ? this.financialYearManager.fetch()
        : { nonHierarchical: [] },
    ]);
    return {
      calendarYear: result[0]['nonHierarchical'],
      financialYear: result[1]['nonHierarchical'],
    };
  };

  process = async (
    data: Record<
      'calendarYear' | 'financialYear',
      CustomSqlQueryValueBackendType[][] | undefined
    >
  ): Promise<HierarchyItem[]> => {
    try {
      const calendarYearItems = await this.calendarYearManager.process({
        nonHierarchical: data['calendarYear'],
      });

      // For financial year, we need to fetch separately if configured
      const financialYearItems = this.financialYearManager
        ? await this.financialYearManager.process({
            nonHierarchical: data['financialYear'],
          })
        : [];

      // Update labels to include context
      const calendarYearItemsWithLabel = calendarYearItems.map((item) => ({
        ...item,
        labelFn: (t: TFunction) => {
          const year = item.name ?? t(UNDEFINED_DISPLAY_KEY);
          return t('common:filterTray.hireYearValues.calendarYearPill', {
            year: year.toString(),
          });
        },
      }));

      const financialYearItemsWithLabel = financialYearItems.map((item) => ({
        ...item,
        labelFn: (t: TFunction) => {
          const year = item.name ?? t(UNDEFINED_DISPLAY_KEY);
          return t('common:filterTray.hireYearValues.financialYearPill', {
            year: year.toString(),
          });
        },
      }));

      // Create hierarchical structure for filter tray
      const hierarchicalItems: HierarchyItem[] = [
        {
          key: hashCode('hire-year-calendar-year'),
          name: 'Calendar Year',
          values: [],
          isSelectable: false,
          dimension: startDateField,
          labelFn: (t) => t('common:filterTray.hireYearValues.calendarYear'),
          subItems: orderBy(calendarYearItemsWithLabel, 'name', 'desc'),
        },
      ];

      if (financialYearItemsWithLabel.length > 0) {
        hierarchicalItems.push({
          key: hashCode('hire-year-financial-year'),
          name: 'Financial Year',
          values: [],
          isSelectable: false,
          dimension: startDateField,
          labelFn: (t) => t('common:filterTray.hireYearValues.financialYear'),
          subItems: orderBy(financialYearItemsWithLabel, 'name', 'desc'),
        });
      }

      console.log('[HireYearFilterDataManager] Processed hire year data', {
        calendarYearCount: calendarYearItemsWithLabel.length,
        financialYearCount: financialYearItemsWithLabel.length,
      });

      return hierarchicalItems;
    } catch (error) {
      console.error(
        '[HireYearFilterDataManager] Error processing hire year data',
        error
      );
      throw error;
    }
  };

  count = (hierarchyItem: HierarchyItem[]) =>
    sum(hierarchyItem.map((i) => i.subItems?.length ?? 0));
}

export const employmentTemporalitySql = (
  temporalities: EMPLOYMENT_TEMPORALITY[]
) => {
  return `${fieldToSql(employmentTemporalityField)} IN (${temporalities.map((t) => `'${t}'`).join(',')})`;
};

export const hierarchyItemOrd: Ord<HierarchyItem> = {
  compare: (a: HierarchyItem, b: HierarchyItem): Ordering => {
    if (a.name === null) {
      return Ordering.GT;
    } else if (b.name === null) {
      return Ordering.LT;
    } else {
      // Configuration for Natual ordering of strings
      // So [x1,x11,x12,x2,x3] => [x1,x2,x3,x11,x12]
      const collator = new Intl.Collator(undefined, {
        numeric: true,
        sensitivity: 'base',
      });
      return collator.compare(a.name, b.name);
    }
  },
};

export class EmployeeCohortFilterDataManager
  implements FilterDataManager<'employeeCohort'>
{
  constructor(
    readonly dimension: DataFieldWithDataType,
    readonly cohortValues: CustomSqlQueryValueBackendType[],
    readonly preferences: DomainPreferences
  ) {}

  queryKey = [
    'get-employee-cohort-values',
    Hasher.hash(this.dimension),
    Hasher.hash(this.cohortValues),
    Hasher.hash(this.preferences),
  ];

  // TODO: fix ts type
  fetch = () => {
    return Promise.resolve({ employeeCohort: [this.cohortValues] });
  };
  process = (
    data: Record<
      'employeeCohort',
      CustomSqlQueryValueBackendType[][] | undefined
    >
  ): Promise<HierarchyItem[]> => {
    // Copied this bit from NonHierarchicalFilterDataManager
    const result =
      data['employeeCohort']?.flatMap((d) =>
        d.map((v) => {
          const value = parseCustomSqlValue(v)?.toString() ?? null;
          const item: HierarchyItem = {
            key: getFilterKey(this.dimension, [value]),
            name: value,
            values: [value],
            dimension: this.dimension,
            labelFn: (t, defaultValue) => {
              return nonHierarchicalFieldLabel(
                t,
                value,
                this.dimension,
                defaultValue
              );
            },
            isSelectable: true,
          };
          return item;
        })
      ) ?? [];
    return Promise.resolve(sortHierarchy(result, this.preferences, 1));
  };

  count = (hierarchyItem: HierarchyItem[]) => getHierarchyLength(hierarchyItem);
}
